import { omit, isEmpty } from "lodash";
import { createAction } from "redux-actions";
import { toast } from 'react-semantic-toasts';
import api from "utils/resources/requisitionLists";
import { put, call, select, takeEvery } from "@redux-saga/core/effects";
import * as commonActionHandlers from "utils/redux/commonActionHandlers";
import { saveRLFilters, loadRLFilters } from './utils/filtersPersistence';

import Api from "../../utils/resourcesApi";

const extendedConstants = (baseConstants) => ({
  ...baseConstants,
  SET_LOADING: "@REQUISITION_LISTS/SET_LOADING",
  SET_TOTALS: "@REQUISITION_LISTS/SET_TOTALS",
  SET_QUOTES: "@REQUISITION_LISTS/SET_QUOTES",
  REMOVE_QUOTES: "@REQUISITION_LISTS/REMOVE_QUOTES",
  SET_QUOTE_ITEMS: "@REQUISITION_LISTS/SET_QUOTE_ITEMS",
  GET_QUOTES_REQUEST: "@REQUISITION_LISTS/GET_QUOTES_REQUEST",
  SET_QUOTES_WITH_PRICE: "@REQUISITION_LISTS/SET_QUOTES_WITH_PRICE",
  REMOVE_QUOTE_ITEMS: "@REQUISITION_LISTS/REMOVE_QUOTE_ITEMS",
  SET_ACTIVE_ITEM_ROW: "@REQUISITION_LISTS/SET_ACTIVE_ITEM_ROW",
  SET_ACTIVE_QUOTE_ROW: "@REQUISITION_LISTS/SET_ACTIVE_QUOTE_ROW",
  CLEAR_ACTIVE_ITEM_ROW: "@REQUISITION_LISTS/CLEAR_ACTIVE_ITEM_ROW",
  CLEAR_ACTIVE_QUOTE_ROW: "@REQUISITION_LISTS/CLEAR_ACTIVE_QUOTE_ROW",
  SET_STATUS_FILTER: "@REQUISITION_LISTS/SET_STATUS_FILTER",
  CREATE_REQUEST: "@REQUISITION_LISTS/CREATE_REQUEST",
  CREATE_REQUEST_SUCCESS: "@REQUISITION_LISTS/CREATE_REQUEST_SUCCESS",
  CREATE_REQUEST_FAILED: "@REQUISITION_LISTS/CREATE_REQUEST_FAILED",
  LOAD_WITH_DEFAULT_FILTER: "@REQUISITION_LISTS/LOAD_WITH_DEFAULT_FILTER",
  CANCEL_REQUEST: "@REQUISITION_LISTS/CANCEL_REQUEST",
  CANCEL_SUCCESS: "@REQUISITION_LISTS/CANCEL_SUCCESS",
  CANCEL_FAILED: "@REQUISITION_LISTS/CANCEL_FAILED",
  SEND_QUOTATION_REMINDER: "@REQUISITION_LISTS/SEND_QUOTATION_REMINDER",
  SET_VIEW_MODE: "@REQUISITION_LISTS/SET_VIEW_MODE",
  GET_ORDER_ESTIMATE_REQUEST: "@REQUISITION_LISTS/GET_ORDER_ESTIMATE_REQUEST",
  SET_ESTIMATE_PRICES: "@REQUISITION_LISTS/SET_ESTIMATE_PRICES",
  REMOVE_ESTIMATE_PRICES: "@REQUISITION_LISTS/REMOVE_ESTIMATE_PRICES",
  GET_ROB_REQUEST: "@REQUISITION_LISTS/GET_ROB_REQUEST",
  SET_ROB: "@REQUISITION_LISTS/SET_ROB",
  REMOVE_ROB: "@REQUISITION_LISTS/REMOVE_ROB",
  GET_PRICE_HISTORY_REQUEST: "@REQUISITION_LISTS/GET_PRICE_HISTORY_REQUEST",
  SET_PRICE_HISTORY: "@REQUISITION_LISTS/SET_PRICE_HISTORY",
  REMOVE_PRICE_HISTORY: "@REQUISITION_LISTS/REMOVE_PRICE_HISTORY",
});

const extendedActions = (baseActions, constants) => ({
  ...baseActions,
  setLoading: createAction(constants.SET_LOADING),
  setTotals: createAction(constants.SET_TOTALS),
  getQuotes: createAction(constants.GET_QUOTES_REQUEST),
  sendQuotationReminder: createAction(constants.SEND_QUOTATION_REMINDER),
  setQuotes: createAction(constants.SET_QUOTES),
  removeQuotes: createAction(constants.REMOVE_QUOTES),
  setActiveItemRow: createAction(constants.SET_ACTIVE_ITEM_ROW),
  setActiveQuoteRow: createAction(constants.SET_ACTIVE_QUOTE_ROW),
  setQuoteItems: createAction(constants.SET_QUOTE_ITEMS),
  setQuotesWithPrice: createAction(constants.SET_QUOTES_WITH_PRICE),
  removeQuoteItems: createAction(constants.REMOVE_QUOTE_ITEMS),
  clearActiveItemRow: createAction(constants.CLEAR_ACTIVE_ITEM_ROW),
  clearActiveQuoteRow: createAction(constants.CLEAR_ACTIVE_QUOTE_ROW),
  setStatusFilter: createAction(constants.SET_STATUS_FILTER),
  createRequest: createAction(constants.CREATE_REQUEST),
  createRequestSuccess: createAction(constants.CREATE_REQUEST_SUCCESS),
  createRequestFailed: createAction(constants.CREATE_REQUEST_FAILED),
  loadWithDefaultFilter: createAction(constants.LOAD_WITH_DEFAULT_FILTER),
  cancelRequest: createAction(constants.CANCEL_REQUEST),
  cancelSuccess: createAction(constants.CANCEL_SUCCESS),
  cancelFailed: createAction(constants.CANCEL_FAILED),
  getOrderEstimate: createAction(constants.GET_ORDER_ESTIMATE_REQUEST),
  setEstimatePrices: createAction(constants.SET_ESTIMATE_PRICES),
  removeEstimatePrices: createAction(constants.REMOVE_ESTIMATE_PRICES),
  getRob: createAction(constants.GET_ROB_REQUEST),
  setRob: createAction(constants.SET_ROB),
  removeRob: createAction(constants.REMOVE_ROB),
  getPriceHistory: createAction(constants.GET_PRICE_HISTORY_REQUEST),
  setPriceHistory: createAction(constants.SET_PRICE_HISTORY),
  removePriceHistory: createAction(constants.REMOVE_PRICE_HISTORY),
});

const extendedInitialState = {
  ...commonActionHandlers.initialState,
  loading: false,
  search: localStorage.getItem("@REQUISITION_LISTS/CHANGE_SEARCH") || "",
  viewMode: localStorage.getItem("@REQUISITION_LISTS/VIEW_MODE") || "rl",
  totals: {
    total: 0,
    paid: 0,
    unpaid: 0,
  },
  quotes: [],
  activeQuoteRow: {},
  activeItemRow: {},
  quoteItems: [],
  quotesWithPrice: [],
  estimatePrices: {},
  rob: {},
  priceHistory: {},
  statusFilter: (() => {
    const loadedFilters = loadRLFilters();
    return {
      type: "select",
      value: loadedFilters.status || ["open", "requested", "offered", "ordered", "in_transit"],
    };
  })(),
  filter: (() => {
    const loadedFilters = loadRLFilters();
    const filter = {};
    if (loadedFilters.rl_number) filter.number = loadedFilters.rl_number;
    if (loadedFilters.vessel_imo) filter.vessel_imo = loadedFilters.vessel_imo;
    if (loadedFilters.importance) filter.importance = loadedFilters.importance;
    if (loadedFilters.status) filter.status = loadedFilters.status.join(",");
    return filter;
  })(),
  sort: { field: 'number', direction: 'desc' },
  pagination: (() => {
    const loadedFilters = loadRLFilters();
    return {
      activePage: 1,
      totalPages: 1,
      ...loadedFilters.pagination,
      itemsPerPage: 20
    };
  })(),
};

const extendedActionHandlers = (baseActionHandlers, constants) => ({
  ...baseActionHandlers,
  [constants.CHANGE_SEARCH]: (state, action) => {
    localStorage.setItem(constants.CHANGE_SEARCH, action.payload);
    saveRLFilters({ search: action.payload });
    return commonActionHandlers.changeSearch(state, action);
  },
  [constants.CLEAR_SEARCH]: (state, action) => {
    localStorage.setItem(constants.CHANGE_SEARCH, "");
    saveRLFilters({ search: "" });
    return commonActionHandlers.clearSearch(state, action);
  },
  [constants.CHANGE_FILTER]: (state, action) => {
    const newFilter = { ...state.filter, [action.payload.field]: action.payload.value };
    saveRLFilters({
      rl_number: newFilter.number,
      vessel_imo: newFilter.vessel_imo,
      importance: newFilter.importance,
    });
    return commonActionHandlers.changeFilter(state, action);
  },
  [constants.REMOVE_FILTER]: (state, action) => {
    const result = commonActionHandlers.removeFilter(state, action);
    const newFilter = result.filter;
    saveRLFilters({
      rl_number: newFilter.number,
      vessel_imo: newFilter.vessel_imo,
      importance: newFilter.importance,
    });
    return result;
  },
  [constants.SET_TOTALS]: (state, { payload }) => ({
    ...state,
    totals: payload,
  }),
  [constants.SET_LOADING]: (state, { payload }) => ({
    ...state,
    loading: payload,
  }),
  [constants.SET_VIEW_MODE]: (state, { payload }) => ({
    ...state,
    viewMode: payload,
  }),
  [constants.SET_QUOTES]: (state, { payload }) => ({
    ...state,
    quotes: { ...state.quotes, ...payload },
  }),
  [constants.REMOVE_QUOTES]: (state, { payload }) => ({
    ...state,
    quotes: omit(state.quotes, payload),
  }),
  [constants.SET_QUOTE_ITEMS]: (state, { payload }) => ({
    ...state,
    quoteItems: { ...state.quoteItems, ...payload },
  }),
  [constants.SET_QUOTES_WITH_PRICE]: (state, { payload }) => ({
    ...state,
    quotesWithPrice: { ...state.quotesWithPrice, ...payload },
  }),
  [constants.REMOVE_QUOTE_ITEMS]: (state, { payload }) => ({
    ...state,
    quoteItems: omit(state.quoteItems, payload),
  }),
  [constants.SET_ACTIVE_ITEM_ROW]: (state, { payload }) => ({
    ...state,
    activeItemRow: { ...state.activeItemRow, ...payload },
  }),
  [constants.SET_ACTIVE_QUOTE_ROW]: (state, { payload }) => ({
    ...state,
    activeQuoteRow: { ...state.activeQuoteRow, ...payload },
  }),
  [constants.CLEAR_ACTIVE_ITEM_ROW]: (state, { payload }) => ({
    ...state,
    activeItemRow: omit(state.activeItemRow, payload),
  }),
  [constants.CLEAR_ACTIVE_QUOTE_ROW]: (state, { payload }) => ({
    ...state,
    activeQuoteRow: omit(state.activeQuoteRow, payload),
  }),
  [constants.SET_STATUS_FILTER]: (state, { payload }) => {
    saveRLFilters({ status: payload.value });
    return {
      ...state,
      statusFilter: payload,
    };
  },
  [constants.CREATE_REQUEST_FAILED]: (state, { payload }) => ({
    ...state,
    errors: payload,
  }),
  [constants.CANCEL_FAILED]: (state, {payload}) => ({
    ...state,
    errors: payload,
  }),
  [constants.SET_ESTIMATE_PRICES]: (state, { payload }) => ({
    ...state,
    estimatePrices: { ...state.estimatePrices, ...payload },
  }),
  [constants.REMOVE_ESTIMATE_PRICES]: (state, { payload }) => ({
    ...state,
    estimatePrices: omit(state.estimatePrices, payload),
  }),
  [constants.SET_ROB]: (state, { payload }) => ({
    ...state,
    rob: { ...state.rob, ...payload },
  }),
  [constants.REMOVE_ROB]: (state, { payload }) => ({
    ...state,
    rob: omit(state.rob, payload),
  }),
  [constants.SET_PRICE_HISTORY]: (state, { payload }) => ({
    ...state,
    priceHistory: { ...state.priceHistory, ...payload },
  }),
  [constants.REMOVE_PRICE_HISTORY]: (state, { payload }) => ({
    ...state,
    priceHistory: omit(state.priceHistory, payload),
  }),
});

const newSagas = (baseSagas, constants, actions) => {
  function* applyStatusFilter({ payload: { type, value } }) {
    try {
      const statuses = type === "all" ? "all" : value.join(",");

      yield put(actions.changeFilter("status", statuses));
      yield put(actions.applyFilter());
    } catch (e) {
      console.error(e);
      yield put(actions.setErrors(e));
    }
  }

  function* getQuotes({ payload: { requisitionListId, cb } }) {
    try {
      const attributes = {
        filter: {
          requisition_list_id: requisitionListId,
        },
      };

      const { data } = yield call(Api.quotations.index, attributes);
      yield put(actions.setQuotes({ [requisitionListId]: data.data }));

      if (cb) yield call(cb);
    } catch (e) {
      console.error(e);
      yield put(actions.setErrors(e));
    }
  }

  // function* setTotals({ payload }) {
  //   try {
  //     const totals = get(payload, 'data.meta.totals');
  //     yield put(actions.setTotals(totals));
  //   } catch (e) {
  //     console.error(e);
  //     yield put(actions.setErrors(e));
  //   }
  // }

  function* createRequest({ payload: { cb, ...payload } }) {
    try {
      const response = yield call(api.createRequest, payload);
      yield put(actions.createRequestSuccess(response.data));
      if (cb) yield call(cb);
      yield put(actions.loadItemsRequest());
    } catch (e) {
      console.error(e);
      yield put(actions.createRequestFailed(e));
    }
  }

  function* sendQuotationReminder({ payload: { quotationId, supplierName, requisitionListId, cb } }) {
    try {
      yield call(api.sendQuotationReminder, quotationId);

      if (cb) yield call(cb);
      yield put(actions.loadItemsRequest());
      // Refresh the quotes of the expanded RL so the "last reminder" popup shows the new date.
      if (requisitionListId) yield put(actions.getQuotes({ requisitionListId }));
      yield call(toast, { type: 'success', title: `Reminder sent successfully to ${supplierName}!` });
    } catch (e) {
      console.error(e);
      yield put(actions.setErrors(e));
      yield call(toast, { type: 'error', title: `Failed to send a reminder to ${supplierName}.` });
    }
  }

  function* requestWithDefaultFilter() {
    try {
      const filter = yield select((state) => state.requisitionLists?.filter);
      if (isEmpty(filter)) {
        const statusFilter = yield select(
          (state) => state.requisitionLists.statusFilter.value
        );
        const statuses = statusFilter.join(",");

        yield put(actions.changeFilter("status", statuses));
        yield put(actions.applyFilter());
      } else {
        yield put(actions.loadItemsRequest());
      }
    } catch (e) {
      console.error(e);
      yield put(actions.setErrors(e));
    }
  }


  function* cancelRequest({payload: {cb, id}}) {
    try {
      const response = yield call(api.cancel, id);
      yield put(actions.cancelSuccess(response.data));
      if (cb) yield call(cb);
      yield put(actions.loadItemsRequest());
    } catch (e) {
      console.error(e);
      yield put(actions.cancelFailed(e));
    }
  }

  function* getOrderEstimate({ payload: { requisitionListId } }) {
    try {
      const { data } = yield call(api.orderEstimate, requisitionListId);
      yield put(actions.setEstimatePrices({ [requisitionListId]: data }));
    } catch (e) {
      console.error(e);
    }
  }

  function* getPriceHistory({ payload: { requisitionListId } }) {
    try {
      const { data } = yield call(api.priceHistory, requisitionListId);
      yield put(actions.setPriceHistory({ [requisitionListId]: data }));
    } catch (e) {
      console.error(e);
    }
  }

  function* getRob({ payload: { requisitionListId } }) {
    try {
      const { data } = yield call(api.rob, requisitionListId);
      yield put(actions.setRob({ [requisitionListId]: data }));
    } catch (e) {
      console.error(e);
    }
  }

  return [
    takeEvery(constants.SET_STATUS_FILTER, applyStatusFilter),
    takeEvery(constants.GET_QUOTES_REQUEST, getQuotes),
    takeEvery(constants.LOAD_WITH_DEFAULT_FILTER, requestWithDefaultFilter),
    takeEvery(constants.CREATE_REQUEST, createRequest),
    takeEvery(constants.CANCEL_REQUEST, cancelRequest),
    takeEvery(constants.SEND_QUOTATION_REMINDER, sendQuotationReminder),
    takeEvery(constants.GET_ORDER_ESTIMATE_REQUEST, getOrderEstimate),
    takeEvery(constants.GET_ROB_REQUEST, getRob),
    takeEvery(constants.GET_PRICE_HISTORY_REQUEST, getPriceHistory),
  ];
};

const extendedSagas = (baseSagas, newSagas) => () => [...baseSagas(), ...newSagas];

const extendedSelectors = (baseSelectors) => (state) => ({
  ...baseSelectors(state),
  filter: state.requisitionLists.filter,
  viewMode: state.requisitionLists.viewMode,
  totals: state.requisitionLists.totals,
  quotes: state.requisitionLists.quotes,
  quoteItems: state.requisitionLists.quoteItems,
  quotesWithPrice: state.requisitionLists.quotesWithPrice,
  activeItemRow: state.requisitionLists.activeItemRow,
  activeQuoteRow: state.requisitionLists.activeQuoteRow,
  estimatePrices: state.requisitionLists.estimatePrices,
  rob: state.requisitionLists.rob,
  priceHistory: state.requisitionLists.priceHistory,
  statusFilter: state.requisitionLists.statusFilter,
});

export default {
  extendedConstants,
  extendedActions,
  extendedActionHandlers,
  extendedSagas,
  newSagas,
  extendedSelectors,
  extendedInitialState,
};
